import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
    Typography,
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Grid,
    IconButton,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
    FormControlLabel,
    Tooltip,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Info as InfoIcon
} from '@mui/icons-material';

const SelectorConfigStep = ({
    scraperConfig,
    setScraperConfig,
    eshopData,
    testProductLoadingMutation,
    testProductLoadingResult,
    isTestingProductLoading,
    handleTestProductLoading,
    handleAnalyzeAndSuggest,
    categories,
    setCategories
}, ref) => {
    const [selectors, setSelectors] = useState({});
    const [conversions, setConversions] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loadDetailPages, setLoadDetailPages] = useState(
        scraperConfig?.load_detail_pages || false
    );

    // Track the last test result ID to detect new tests
    const lastTestResultRef = useRef(null);
    // Track if modal was manually closed for current result
    const wasManuallyClosedRef = useRef(false);

    // Simple function to open modal
    const handleOpenPreview = useCallback(() => {
        setIsModalOpen(true);
        wasManuallyClosedRef.current = false;
    }, []);

    // Simple function to close modal
    const handleClosePreview = useCallback(() => {
        setIsModalOpen(false);
        wasManuallyClosedRef.current = true;
    }, []);

    // Expose the handleOpenPreview function to the parent component
    useImperativeHandle(ref, () => ({
        handleOpenPreview
    }));

    // Update local state when scraperConfig changes
    useEffect(() => {
        setLoadDetailPages(scraperConfig?.load_detail_pages || false);
    }, [scraperConfig]);

    // Common e-commerce elements
    const commonElements = [
        { id: 'product_list', label: 'Product List', description: 'Container for product listings' },
        { id: 'product_container', label: 'Product Container', description: 'Individual product container' },
        { id: 'product_name', label: 'Product Name', description: 'Name/title of the product' },
        { id: 'price_per_piece', label: 'Price per Piece', description: 'Price for individual items (e.g., .price, .product-price, .cost, [data-price])' },
        { id: 'stock_info', label: 'Stock Information', description: 'Availability/stock status' },
        { id: 'manufacturer', label: 'Manufacturer', description: 'Product manufacturer/brand' },
        { id: 'product_number', label: 'Product Number', description: 'SKU or product identifier' },
        { id: 'unique_id', label: 'Unique ID', description: 'Unique identifier for the product' },
        { id: 'product_image', label: 'Product Image', description: 'Main product image' },
        { id: 'product_description', label: 'Product Description', description: 'Detailed product description' },
        { id: 'category', label: 'Category', description: 'Product category' },
        { id: 'next_button', label: 'Next Page Button', description: 'Button to navigate to next page' },
        { id: 'model_sku', label: 'Model/SKU', description: 'Product model or SKU' },
        { id: 'price_per_wp', label: 'Price per Wp', description: 'Price per watt peak' },
        { id: 'product_link', label: 'Product Link', description: 'Link to product detail page (required if visiting detail pages)' }
    ];

    // Update selectors and conversions when config changes
    useEffect(() => {
        if (scraperConfig) {
            // Normalize selectors to handle both old format (string/number) and new format (object with pageType and update)
            const normalizedSelectors = {};
            const configSelectors = scraperConfig?.selectors || {};
            Object.keys(configSelectors).forEach(key => {
                const value = configSelectors[key];
                if (typeof value === 'string' || typeof value === 'number') {
                    // Old format: just the selector string/number
                    normalizedSelectors[key] = { 
                        selector: String(value), 
                        pageType: 'list',
                        update: true // Default to true for old format
                    };
                } else if (typeof value === 'object' && value !== null) {
                    // New format or object: use value.selector if present, otherwise default to empty string
                    const selectorStr = (typeof value.selector === 'string' || typeof value.selector === 'number')
                        ? String(value.selector)
                        : '';
                    normalizedSelectors[key] = {
                        selector: selectorStr,
                        pageType: value.pageType || 'list',
                        update: value.update !== undefined ? value.update : true // Default to true if not specified
                    };
                } else {
                    // Fallback for any other format
                    normalizedSelectors[key] = { 
                        selector: '', 
                        pageType: 'list',
                        update: true
                    };
                }
            });
            setSelectors(normalizedSelectors);
            
            // Set conversions from config
            setConversions(scraperConfig.conversions || []);
            
            setLoadDetailPages(scraperConfig?.load_detail_pages || false);
        }
    }, [scraperConfig]);

    // Auto-open modal when NEW test results arrive
    useEffect(() => {
        if (testProductLoadingResult) {
            const currentResultId = JSON.stringify(testProductLoadingResult);

            // Only open if this is a genuinely new test result AND wasn't manually closed
            if (currentResultId !== lastTestResultRef.current) {
                lastTestResultRef.current = currentResultId;
                wasManuallyClosedRef.current = false;
                setIsModalOpen(true);
            } else if (!wasManuallyClosedRef.current) {
                // If it's the same result but wasn't manually closed, keep it closed
                setIsModalOpen(false);
            }
        }
    }, [testProductLoadingResult]);

    // Handle selector change
    const handleSelectorChange = (elementId, value) => {
        // Ensure value is a string (in case of empty input or object)
        const selectorValue = (typeof value === 'string' || typeof value === 'number') ? String(value) : '';
        const currentSelector = selectors[elementId] || { selector: '', pageType: 'list', update: true };
        const newSelectors = {
            ...selectors,
            [elementId]: {
                ...currentSelector,
                selector: selectorValue
            }
        };

        // Deep-clone selectors before setting global config
        let clonedSelectors;
        try {
            clonedSelectors = JSON.parse(JSON.stringify(newSelectors));
        } catch (err) {
            clonedSelectors = Object.keys(newSelectors).reduce((acc, k) => {
                const s = newSelectors[k];
                acc[k] = {
                    selector: (typeof s?.selector === 'string' || typeof s?.selector === 'number') ? String(s.selector) : '',
                    pageType: s?.pageType || 'list',
                    update: s?.update !== undefined ? s.update : true
                };
                return acc;
            }, {});
        }

        setSelectors(clonedSelectors);

        setScraperConfig(prevConfig => ({
            ...prevConfig,
            selectors: clonedSelectors,
            conversions: conversions,
            load_detail_pages: loadDetailPages
        }));
    };

    // Handle selector change for TextField - ensure proper string handling
    const handleSelectorChangeTextField = (elementId, e) => {
        const value = e.target.value;
        handleSelectorChange(elementId, value);
    };

    // Handle page type change
    const handlePageTypeChange = (elementId, pageType) => {
        const currentSelector = selectors[elementId] || { selector: '', pageType: 'list', update: true };
        const newSelectors = {
            ...selectors,
            [elementId]: {
                ...currentSelector,
                pageType: pageType,
                selector: typeof currentSelector.selector === 'string' ? currentSelector.selector : ''
            }
        };
        setSelectors(newSelectors);
        setScraperConfig(prevConfig => ({
            ...prevConfig,
            selectors: newSelectors,
            conversions: conversions,
            load_detail_pages: loadDetailPages
        }));
    };

    // Handle update toggle
    const handleUpdateToggle = (elementId, update) => {
        const currentSelector = selectors[elementId] || { selector: '', pageType: 'list', update: true };
        const newSelectors = {
            ...selectors,
            [elementId]: {
                ...currentSelector,
                update: update
            }
        };
        setSelectors(newSelectors);
        setScraperConfig(prevConfig => ({
            ...prevConfig,
            selectors: newSelectors,
            conversions: conversions,
            load_detail_pages: loadDetailPages
        }));
    };

    // Handle load detail pages toggle
    const handleLoadDetailPagesChange = (enabled) => {
        setLoadDetailPages(enabled);
        setScraperConfig(prevConfig => ({
            ...prevConfig,
            selectors: selectors,
            conversions: conversions,
            load_detail_pages: enabled
        }));
    };
    
    // Conversion Handlers
    const handleAddConversion = () => {
        const newConversion = { 
            field: 'product_number', 
            type: 'replace_text', 
            find: '', 
            replace: '',
            pattern: '',
            group: 1,
            text: ''
        };
        const newConversions = [...conversions, newConversion];
        setConversions(newConversions);
        setScraperConfig(prevConfig => ({
            ...prevConfig,
            selectors: selectors,
            conversions: newConversions,
            load_detail_pages: loadDetailPages
        }));
    };
    
    const handleRemoveConversion = (index) => {
        const newConversions = conversions.filter((_, i) => i !== index);
        setConversions(newConversions);
        setScraperConfig(prevConfig => ({
            ...prevConfig,
            selectors: selectors,
            conversions: newConversions,
            load_detail_pages: loadDetailPages
        }));
    };
    
    const handleUpdateConversion = (index, field, value) => {
        const newConversions = [...conversions];
        newConversions[index] = { ...newConversions[index], [field]: value };
        setConversions(newConversions);
        setScraperConfig(prevConfig => ({
            ...prevConfig,
            selectors: selectors,
            conversions: newConversions,
            load_detail_pages: loadDetailPages
        }));
    };


    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>
                Selector Configuration
            </Typography>

            <Grid container spacing={2}>
                {/* Selector Configuration - Two columns */}
                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                        Selector Identification Tool
                    </Typography>
                    <Grid container spacing={1}>
                        {commonElements.map((element) => (
                            <Grid item xs={12} sm={6} key={element.id}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1, p: 1, border: '1px solid #e0e0', borderRadius: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                            label={element.label}
                                            value={selectors[element.id]?.selector || ''}
                                            onChange={(e) => handleSelectorChangeTextField(element.id, e)}
                                            placeholder={`Enter CSS selector for ${element.label}`}
                                            variant="outlined"
                                            size="small"
                                            sx={{ flex: 1 }}
                                            helperText={element.description}
                                        />
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={selectors[element.id]?.pageType === 'detail'}
                                                        onChange={(e) => handlePageTypeChange(element.id, e.target.checked ? 'detail' : 'list')}
                                                        size="small"
                                                    />
                                                }
                                                label={<Typography variant="caption">Detail Page</Typography>}
                                                sx={{ ml: 0, mb: -1 }}
                                            />
                                            {element.id !== 'product_list' && element.id !== 'product_container' && (
                                                <Tooltip title="If disabled, this field will not be updated for products already in the database. Useful for saving time on detail page extraction.">
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={selectors[element.id]?.update !== false}
                                                                onChange={(e) => handleUpdateToggle(element.id, e.target.checked)}
                                                                size="small"
                                                                color="success"
                                                            />
                                                        }
                                                        label={<Typography variant="caption">Update</Typography>}
                                                        sx={{ ml: 0 }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Custom elements */}
                    {Object.keys(selectors).filter(key =>
                        !commonElements.some(el => el.id === key)
                    ).map((customKey) => (
                        <Grid container item xs={12} sm={6} key={customKey}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1, p: 1, border: '1px solid #e0e0', borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TextField
                                        label="Custom Element"
                                        value={selectors[customKey]?.selector || ''}
                                        onChange={(e) => handleSelectorChangeTextField(customKey, e)}
                                        placeholder="Enter CSS selector"
                                        variant="outlined"
                                        size="small"
                                        sx={{ flex: 1 }}
                                    />
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={selectors[customKey]?.pageType === 'detail'}
                                                    onChange={(e) => handlePageTypeChange(customKey, e.target.checked ? 'detail' : 'list')}
                                                    size="small"
                                                />
                                            }
                                            label={<Typography variant="caption">Detail Page</Typography>}
                                            sx={{ ml: 0, mb: -1 }}
                                        />
                                        <Tooltip title="If disabled, this field will not be updated for products already in the database.">
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={selectors[customKey]?.update !== false}
                                                        onChange={(e) => handleUpdateToggle(customKey, e.target.checked)}
                                                        size="small"
                                                        color="success"
                                                    />
                                                }
                                                label={<Typography variant="caption">Update</Typography>}
                                                sx={{ ml: 0 }}
                                            />
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    ))}


                    {/* Detail Page Configuration Section */}
                    <Grid item xs={12} sx={{ mt: 3, mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Detail Page Configuration
                        </Typography>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Enable this option to visit individual product pages for additional data.
                            <strong> This will significantly increase scraping time</strong> but may
                            provide more accurate product information (e.g., unique product identifiers,
                            detailed specifications).
                        </Alert>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={loadDetailPages}
                                        onChange={(e) => {
                                            const enabled = e.target.checked;
                                            handleLoadDetailPagesChange(enabled);
                                        }}
                                        color="primary"
                                    />
                                }
                                label="Visit Product Detail Pages"
                            />
                            <Tooltip title="When enabled, the scraper will visit each product's detail page to extract additional information. This is useful when critical data (like product numbers) is only available on detail pages.">
                                <InfoIcon color="action" />
                            </Tooltip>
                        </Box>

                        {loadDetailPages && (
                            <Box sx={{ pl: 2, borderLeft: '3px solid #1976d2', ml: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TextField
                                        label="Product Link Selector"
                                        value={selectors.product_link?.selector || ''}
                                        onChange={(e) => handleSelectorChangeTextField('product_link', e)}
                                        placeholder="e.g., .product-title a, .product-link, a.product-url"
                                        variant="outlined"
                                        size="small"
                                        sx={{ flex: 1 }}
                                        helperText="CSS selector for the link to the product detail page. Common patterns: .product-title a, .product-name a, a[href*='/product/']"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={selectors.product_link?.pageType === 'detail'}
                                                onChange={(e) => handlePageTypeChange('product_link', e.target.checked ? 'detail' : 'list')}
                                                size="small"
                                            />
                                        }
                                        label="Detail Page"
                                        sx={{ ml: 0, mt: 0.5, alignSelf: 'center' }}
                                    />
                                </Box>


                            </Box>
                        )}
                    </Grid>

                </Grid>

                {/* Catalog URL and Categories below selectors */}
                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                        Catalog & Categories
                    </Typography>

                    {/* Catalog URL Field */}
                    <Grid item xs={12} sx={{ mb: 2 }}>
                        <TextField
                            label="Catalog URL"
                            value={scraperConfig.catalog_url || ''}
                            onChange={(e) => setScraperConfig(prevConfig => ({
                                ...prevConfig,
                                catalog_url: e.target.value,
                                load_detail_pages: loadDetailPages
                            }))}
                            helperText="URL to the product catalog page (optional, will use base URL if not provided)"
                            size="small"
                            fullWidth
                        />
                    </Grid>

                    {/* Category Configuration Section */}
                    <Typography variant="h6" gutterBottom>
                        Category Configuration
                    </Typography>
                    {categories.map((category, index) => (
                        <Grid container item xs={12} spacing={1} key={index} sx={{ mt: 0.5, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <TextField
                                    label="Category Name"
                                    value={category.name}
                                    onChange={(e) => {
                                        const newCategories = [...categories];
                                        newCategories[index].name = e.target.value;
                                        setCategories(newCategories);
                                    }}
                                    size="small"
                                    sx={{ flex: 1, minWidth: '150px' }}
                                />
                                <TextField
                                    label="Products URL"
                                    value={category.products_url}
                                    onChange={(e) => {
                                        const newCategories = [...categories];
                                        newCategories[index].products_url = e.target.value;
                                        setCategories(newCategories);
                                    }}
                                    size="small"
                                    sx={{ flex: 1, minWidth: '150px' }}
                                />
                                <TextField
                                    label="Default Category"
                                    value={category.category_default}
                                    onChange={(e) => {
                                        const newCategories = [...categories];
                                        newCategories[index].category_default = e.target.value;
                                        setCategories(newCategories);
                                    }}
                                    size="small"
                                    sx={{ flex: 1, minWidth: '150px' }}
                                />
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() => {
                                        if (categories.length > 1) {
                                            const newCategories = categories.filter((_, i) => i !== index);
                                            setCategories(newCategories);
                                        } else {
                                            // If it's the last category, just clear the fields
                                            const newCategories = [...categories];
                                            newCategories[0] = { name: '', products_url: '', category_default: '' };
                                            setCategories(newCategories);
                                        }
                                    }}
                                    disabled={categories.length <= 1}
                                    size="small"
                                    sx={{ flex: '0 0 auto' }}
                                >
                                    Remove
                                </Button>
                            </Grid>
                        </Grid>
                    ))}
                    <Grid item xs={12} sx={{ mt: 1 }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => setCategories([...categories, { name: '', products_url: '', category_default: '' }])}
                            startIcon={<AddIcon />}
                            size="small"
                        >
                            Add Category
                        </Button>
                    </Grid>
                </Grid>
                
                {/* Data Post-Processing Section */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Data Post-Processing
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                        Configure transformations to clean or modify data after extraction. For example, remove "Art.No.:" from a product number.
                    </Typography>
                    
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        {conversions.length === 0 && (
                            <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2, textAlign: 'center' }}>
                                No conversions configured. Click "Add Conversion" to start.
                            </Typography>
                        )}
                        
                        {conversions.map((conv, index) => (
                            <Box key={index} sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: 2, 
                                mb: 2, 
                                p: 2, 
                                border: 1, 
                                borderColor: 'divider', 
                                borderRadius: 1,
                                bgcolor: 'background.default'
                            }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Target Field</InputLabel>
                                            <Select
                                                value={conv.field}
                                                label="Target Field"
                                                onChange={(e) => handleUpdateConversion(index, 'field', e.target.value)}
                                            >
                                                {Object.keys(selectors).map(key => (
                                                    <MenuItem key={key} value={key}>
                                                        {commonElements.find(el => el.id === key)?.label || key}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Conversion Type</InputLabel>
                                            <Select
                                                value={conv.type}
                                                label="Conversion Type"
                                                onChange={(e) => handleUpdateConversion(index, 'type', e.target.value)}
                                            >
                                                <MenuItem value="replace_text">Replace Text</MenuItem>
                                                <MenuItem value="regex_match">Regex Match</MenuItem>
                                                <MenuItem value="regex_replace">Regex Replace</MenuItem>
                                                <MenuItem value="prepend">Prepend Text</MenuItem>
                                                <MenuItem value="append">Append Text</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={5}>
                                        {/* Dynamic inputs based on type */}
                                        {conv.type === 'replace_text' && (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <TextField 
                                                    size="small" 
                                                    label="Find" 
                                                    fullWidth 
                                                    value={conv.find || ''} 
                                                    onChange={(e) => handleUpdateConversion(index, 'find', e.target.value)}
                                                />
                                                <TextField 
                                                    size="small" 
                                                    label="Replace with" 
                                                    fullWidth 
                                                    value={conv.replace || ''} 
                                                    onChange={(e) => handleUpdateConversion(index, 'replace', e.target.value)}
                                                />
                                            </Box>
                                        )}
                                        
                                        {conv.type === 'regex_match' && (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <TextField 
                                                    size="small" 
                                                    label="Regex Pattern" 
                                                    fullWidth 
                                                    placeholder="e.g. Art\.No\.:\s*(\d+)"
                                                    value={conv.pattern || ''} 
                                                    onChange={(e) => handleUpdateConversion(index, 'pattern', e.target.value)}
                                                />
                                                <TextField 
                                                    size="small" 
                                                    label="Group" 
                                                    type="number"
                                                    sx={{ width: '80px' }}
                                                    value={conv.group || 1} 
                                                    onChange={(e) => handleUpdateConversion(index, 'group', e.target.value)}
                                                />
                                            </Box>
                                        )}

                                        {conv.type === 'regex_replace' && (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <TextField 
                                                    size="small" 
                                                    label="Regex Pattern" 
                                                    fullWidth 
                                                    value={conv.pattern || ''} 
                                                    onChange={(e) => handleUpdateConversion(index, 'pattern', e.target.value)}
                                                />
                                                <TextField 
                                                    size="small" 
                                                    label="Replace with" 
                                                    fullWidth 
                                                    value={conv.replace || ''} 
                                                    onChange={(e) => handleUpdateConversion(index, 'replace', e.target.value)}
                                                />
                                            </Box>
                                        )}
                                        
                                        {(conv.type === 'prepend' || conv.type === 'append') && (
                                            <TextField 
                                                size="small" 
                                                label="Text" 
                                                fullWidth 
                                                value={conv.text || ''} 
                                                onChange={(e) => handleUpdateConversion(index, 'text', e.target.value)}
                                            />
                                        )}
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <IconButton 
                                            size="small" 
                                            color="error" 
                                            onClick={() => handleRemoveConversion(index)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        ))}
                        
                        <Button 
                            startIcon={<AddIcon />} 
                            variant="outlined" 
                            size="small" 
                            onClick={handleAddConversion}
                        >
                            Add Conversion
                        </Button>
                    </Paper>
                </Grid>
            </Grid>

            {/* Preview Dialog - Simplified */}
            <Dialog
                open={isModalOpen}
                onClose={handleClosePreview}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Scraper Preview</DialogTitle>
                <DialogContent>
                    {testProductLoadingResult ? (
                        <Box>
                            {testProductLoadingResult.success ? (
                                <Box>
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        {testProductLoadingResult.message}
                                    </Alert>
                                    <Typography variant="h6" gutterBottom>
                                        Found {testProductLoadingResult.products_found || testProductLoadingResult.products?.length || 0} products
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontStyle: 'italic' }}>
                                        Note: Fields showing "N/A" indicate selectors that need to be configured in the selector configuration section above. For price selectors, common patterns include .price, .product-price, .cost, [data-price], .current-price, .special-price, .regular-price, .price-sales, .price-regular, .price-current, .product__price, .price__current, .price__amount. For solar products, look for .price-per-wp, .solar-price-per-wp, .solar-price-per-unit, or similar.
                                    </Typography>
                                    {testProductLoadingResult.sample_products && testProductLoadingResult.sample_products.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Sample Products:
                                            </Typography>
                                            {testProductLoadingResult.sample_products.map((product, index) => (
                                                <Card key={index} sx={{ mb: 2 }}>
                                                    <CardContent>
                                                        <Typography><strong>Name:</strong> {product.product_name}</Typography>
                                                        <Typography><strong>Price:</strong> {product.price_per_piece}</Typography>
                                                        <Typography><strong>Stock:</strong> {product.stock_info}</Typography>
                                                        <Typography><strong>Manufacturer:</strong> {product.manufacturer}</Typography>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </Box>
                                    )}
                                    {testProductLoadingResult.products && testProductLoadingResult.products.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Sample Products (from test):
                                            </Typography>
                                            {testProductLoadingResult.products.map((product, index) => (
                                                <Card key={index} sx={{ mb: 2 }}>
                                                    <CardContent>
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography><strong>Name:</strong> {product.name || product.product_name || 'N/A'}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography><strong>Price:</strong> {product.price || product.price_per_piece || 'N/A (Configure price selector in selectors)'}</Typography>
                                                            </Grid>

                                                            <Grid item xs={12} sm={6}>
                                                                <Typography><strong>Product Number:</strong> {product.product_code || product.product_number || 'N/A (Configure product number selector in selectors)'}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography><strong>Stock Info:</strong> {product.stock_info || 'N/A (Configure stock info selector in selectors)'}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography><strong>Manufacturer:</strong> {product.manufacturer || 'N/A (Configure manufacturer selector in selectors)'}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography><strong>Model/SKU:</strong> {product.model_sku || 'N/A (Configure model/SKU selector in selectors)'}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography><strong>Category:</strong> {product.category || 'N/A (Configure category selector in selectors)'}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography><strong>Price per Wp:</strong> {product.price_per_wp ? `${product.price_per_wp} €/Wp` : 'N/A (Configure price per Wp selector in selectors)'}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography><strong>Image:</strong> {product.image ? <a href={product.image} target="_blank" rel="noopener noreferrer">View Image</a> : 'N/A (Configure image selector in selectors)'}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography><strong>Link:</strong> {product.link || product.product_url || 'N/A (Configure product link selector in selectors)'}</Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </Box>
                                    )}

                                    {/* Append scraper logs if present */}
                                    {(testProductLoadingResult.logs || testProductLoadingResult.structured_logs) && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Scraper Logs
                                            </Typography>
                                            <Box component="pre" sx={{ background: '#f5f5', p: 1, maxHeight: 900, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                                {testProductLoadingResult.logs
                                                    ? testProductLoadingResult.logs
                                                    : JSON.stringify(testProductLoadingResult.structured_logs, null, 2)}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Alert severity="error">
                                    {testProductLoadingResult.message}
                                </Alert>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' onClick={handleClosePreview}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default forwardRef(SelectorConfigStep);